import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  certificatesService,
  CertificateTemplate,
} from '@/services/certificates.service';

export const certificateKeys = {
  all: ['certificates'] as const,
  lists: () => [...certificateKeys.all, 'list'] as const,
  list: (params: any) => [...certificateKeys.lists(), params] as const,
  my: () => [...certificateKeys.all, 'my'] as const,
  detail: (id: string) => [...certificateKeys.all, 'detail', id] as const,
  verify: (code: string) => [...certificateKeys.all, 'verify', code] as const,
  templates: () => [...certificateKeys.all, 'templates'] as const,
  templatesList: (params: any) =>
    [...certificateKeys.templates(), 'list', params] as const,
  template: (id: string) => [...certificateKeys.templates(), id] as const,
};

// User queries
export function useMyCertificates() {
  return useQuery({
    queryKey: certificateKeys.my(),
    queryFn: () => certificatesService.getMyCertificates(),
  });
}

export function useCertificate(id: string, enabled = true) {
  return useQuery({
    queryKey: certificateKeys.detail(id),
    queryFn: () => certificatesService.getCertificateById(id),
    enabled: enabled && !!id,
  });
}

export function useVerifyCertificate(code: string, enabled = true) {
  return useQuery({
    queryKey: certificateKeys.verify(code),
    queryFn: () => certificatesService.verifyCertificate(code),
    enabled: enabled && !!code,
  });
}

// Admin queries
export function useCertificates(
  params: {
    page?: number;
    limit?: number;
    courseId?: string;
    userId?: string;
  } = {},
) {
  return useQuery({
    queryKey: certificateKeys.list(params),
    queryFn: () => certificatesService.findAll(params),
  });
}

export function useCertificateTemplates(
  params: {
    page?: number;
    limit?: number;
    isActive?: boolean;
  } = {},
) {
  return useQuery({
    queryKey: certificateKeys.templatesList(params),
    queryFn: () => certificatesService.getTemplates(params),
  });
}

export function useCertificateTemplate(id: string, enabled = true) {
  return useQuery({
    queryKey: certificateKeys.template(id),
    queryFn: () => certificatesService.getTemplateById(id),
    enabled: enabled && !!id,
  });
}

// User mutations
export function useGenerateMyCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: string) =>
      certificatesService.generateMyCertificate(courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificateKeys.my() });
    },
  });
}

export function useGetCertificateDownloadUrl() {
  return useMutation({
    mutationFn: (certificateId: string) =>
      certificatesService.getDownloadUrl(certificateId),
  });
}

// Admin mutations
export function useGenerateCertificateForUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      userId,
      templateId,
    }: {
      courseId: string;
      userId: string;
      templateId?: string;
    }) =>
      certificatesService.generateCertificateForUser(
        courseId,
        userId,
        templateId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificateKeys.lists() });
    },
  });
}

export function useCreateCertificateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (template: Partial<CertificateTemplate>) =>
      certificatesService.createTemplate(template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificateKeys.templates() });
    },
  });
}

export function useUpdateCertificateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...template
    }: Partial<CertificateTemplate> & { id: string }) =>
      certificatesService.updateTemplate(id, template),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: certificateKeys.templates() });
      queryClient.invalidateQueries({
        queryKey: certificateKeys.template(variables.id),
      });
    },
  });
}

export function useDeleteCertificateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => certificatesService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificateKeys.templates() });
    },
  });
}
